import React, { useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./AppLayout.module.scss";
import MainContent from "./MainContent";
import MobileHeader from "./MobileHeader";
import useAppModernisation from "../../hooks/useAppModernisation";
import useTopicSidebarToggle from "../../hooks/useTopicSidebarToggle";
import useScrollContainer from "../../contexts/ScrollContext";
import useScrollDirection from "../../hooks/useScrollDirection";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setHeaderHidden } from "../../features/topic/ContentSideBarSlice";

const AppLayout = ({
  children,
  appSidebar,
  pageSidebar,
  showMainSidebar = true,
  showSidebar = true,
  sidebarHidden = false, // New prop to control sidebar visibility
  className = "",
  useMainContentWrapper = true,
  stickyPageSidebar,
  appHeader,
  stickyPageHeader,
  showMobileHeader = true, // New prop to control MobileHeader visibility
}) => {
  const { isCalculatorsLegacyIntegration } = useAppModernisation();
  const { isOpen: isSidebarOpen } = useTopicSidebarToggle();
  const scrollContainerRef = useScrollContainer();
  const location = useLocation();
  const scrollDirection = useScrollDirection(10, scrollContainerRef);
  const headerRef = React.useRef(null);

  // Only apply scroll behavior on topic pages
  const isTopicPage =
    location.pathname.includes("/viewTopic") ||
    location.search.includes("topicfile=");

  // Add/remove body class for viewTopic pages to control page-level scrolling
  React.useEffect(() => {
    if (isTopicPage) {
      document.body.classList.add("view-topic-page");
    } else {
      document.body.classList.remove("view-topic-page");
    }
    return () => {
      document.body.classList.remove("view-topic-page");
    };
  }, [isTopicPage]);

  // Check if we're near the top of the page
  const [isNearTop, setIsNearTop] = React.useState(true);
  const [hasEnoughContent, setHasEnoughContent] = React.useState(true);

  React.useEffect(() => {
    const checkScrollPosition = () => {
      const scrollY = scrollContainerRef?.current
        ? scrollContainerRef.current.scrollTop
        : window.pageYOffset || document.documentElement.scrollTop;
      setIsNearTop(scrollY < 100); // Show header when within 100px of top
    };

    const checkContentHeight = () => {
      const scrollHeight = scrollContainerRef?.current
        ? scrollContainerRef.current.scrollHeight
        : document.documentElement.scrollHeight;
      const clientHeight = scrollContainerRef?.current
        ? scrollContainerRef.current.clientHeight
        : window.innerHeight;

      const headerHeight = headerRef.current
        ? headerRef.current.offsetHeight
        : 0;

      const scrollableHeight = scrollHeight - clientHeight;
      const threshold = 2 * headerHeight;
      setHasEnoughContent(scrollableHeight > threshold + 1);
    };

    const scrollElement = scrollContainerRef?.current || window;
    scrollElement.addEventListener("scroll", checkScrollPosition, {
      passive: true,
    });
    checkScrollPosition(); // Check initial position
    checkContentHeight();
    const timeoutId = setTimeout(checkContentHeight, 2000);

    // Recheck content height when window resizes or content changes
    const resizeObserver = new ResizeObserver(checkContentHeight);
    const targetElement = scrollContainerRef?.current || document.body;
    resizeObserver.observe(targetElement);

    return () => {
      scrollElement.removeEventListener("scroll", checkScrollPosition);
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [scrollContainerRef, isTopicPage]);

  // Always show header when near top, otherwise hide when scrolling down
  // Only apply hiding if there's sufficient content to scroll
  const shouldHideHeaders =
    scrollContainerRef &&
    isTopicPage &&
    !isNearTop &&
    scrollDirection === "down" &&
    hasEnoughContent;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setHeaderHidden(shouldHideHeaders));
  }, [shouldHideHeaders]);

  // Add conditional styling
  const containerClasses = [
    styles.appContainer,
    // FOR DROP 2: Only hide main content when NOT in calculator legacy integration mode
    // In Drop 2 (calculator integration), both sidebar and content should be visible
    showSidebar &&
      pageSidebar &&
      !sidebarHidden &&
      !isCalculatorsLegacyIntegration
      ? styles.hideMainContent
      : "",
    // Add Drop 2 specific class when calculator integration is enabled
    isCalculatorsLegacyIntegration ? styles.calculatorIntegrationLayout : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const leftColumnClasses = [
    styles.leftColumn,
    sidebarHidden ? styles.hidden : "",
    stickyPageSidebar ? styles.stickyLeftColumn : "",
    stickyPageSidebar && !isSidebarOpen ? styles.sidebarClosed : "",
  ]
    .filter(Boolean)
    .join(" ");

  const rightColumnClasses = [
    styles.rightColumn,
    "rightColumn",
    stickyPageSidebar && !isSidebarOpen ? styles.sidebarClosed : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClasses}>
      {/* App Header - Fixed at top on mobile/tablet ONLY */}
      <div className={styles.appHeaderContainer}>
        {showMobileHeader && <MobileHeader></MobileHeader>}
        {stickyPageHeader && <div>{stickyPageHeader}</div>}
      </div>

      {/* Content wrapper for sidebar and main content */}
      <div className={styles.contentWrapper}>
        {/* Main Application Sidebar */}
        {showMainSidebar && appSidebar && (
          <div className={styles.appSidebar}>{appSidebar}</div>
        )}

        {/* Section Sidebar */}
        {showSidebar && pageSidebar && (
          <div className={leftColumnClasses}>{pageSidebar}</div>
        )}

        {/* Section Sidebar */}
        {showSidebar && stickyPageSidebar && (
          <div className={leftColumnClasses}>{stickyPageSidebar}</div>
        )}

        {/* Content Area */}
        <div className={rightColumnClasses}>
          {/* Right column header - Search and Breadcrumb */}
          {appHeader && (
            <div
              ref={headerRef}
              className={`${styles.rightColumnHeader} ${shouldHideHeaders ? styles.hidden : ""}`}
            >
              {appHeader}
            </div>
          )}

          {/* Main Content */}
          {useMainContentWrapper ? (
            <MainContent>{children}</MainContent>
          ) : (
            children
          )}
        </div>
      </div>
      {/* Copyright info for printing */}
      <div className={styles.onlyPrint}>
        © {new Date().getFullYear()} Therapeutic Guidelines Limited. All rights
        reserved.
      </div>
    </div>
  );
};

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
  appSidebar: PropTypes.node,
  pageSidebar: PropTypes.node,
  showMainSidebar: PropTypes.bool,
  showSidebar: PropTypes.bool,
  sidebarHidden: PropTypes.bool,
  appHeader: PropTypes.node,
  className: PropTypes.string,
  useMainContentWrapper: PropTypes.bool,
  showMobileHeader: PropTypes.bool,
};

export default AppLayout;
